import React, { createContext, useContext, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useLocation, useHistory } from "react-router-dom";
import { getUserPermissionsList, isCaregiver, getMenu, getCurrentPage } from "../selectors";
import { PERMISSIONS } from "../constants/rolemapping";

type LayoutType = "default" | "client";

interface LayoutContextType {
  layout: LayoutType;
  setLayout: (layout: LayoutType) => void;
  isCheckingPermission: boolean;
}

const LayoutContext = createContext<LayoutContextType | undefined>(undefined);

export const useLayout = () => {
  const context = useContext(LayoutContext);
  if (!context) {
    throw new Error("useLayout must be used within a LayoutProvider");
  }
  return context;
};

interface LayoutProviderProps {
  children: React.ReactNode;
}

export const LayoutProvider: React.FC<LayoutProviderProps> = ({ children }) => {
  const userPermissions = useSelector(getUserPermissionsList);
  const isClientTypeUser = useSelector(isCaregiver);
  const location = useLocation();
  const history = useHistory();
  const menu = useSelector(getMenu);
  const currentPage = useSelector(getCurrentPage);
  const [layout, setLayout] = useState<LayoutType>("default");
  const [isCheckingPermission, setIsCheckingPermission] = useState(true);

  useEffect(() => {
    // Routes that should not display any header/sidenav (I got them from Header/index.js and SideNav/index.js)
    const routesWithoutHeader = [
      "/",
      "/force-password-change",
      "/account-selection",
      "/reset-password",
      "/forgot-password",
      "/test",
    ];

    const shouldUseDefaultLayout = routesWithoutHeader.some(
      (route) =>
        location.pathname === route ||
        location.pathname.includes("/setup/") ||
        location.pathname.includes("/accept-invitation/") ||
        location.pathname.includes("/static-video-page") ||
        // location.pathname.includes("/video/") ||
        location.pathname.includes("/rescheduling-session") ||
        location.pathname.includes("/document-viewer/") ||
        // Experiment launch route (opens the lab in a new tab): no header/sidenav.
        location.pathname.startsWith("/experiments/")
    );

    // For routes that don't need permission checks, always use default layout
    if (shouldUseDefaultLayout) {
      setLayout("default");
      setIsCheckingPermission(false);
      return;
    }

    // Only determine layout when we have permissions - prevents flashing
    if (userPermissions) {
      const shouldUseClientLayout =
        userPermissions.view_client_dashboard &&
        !userPermissions.view_admin_dashboard &&
        !userPermissions.view_clinician_dashboard;

      setLayout(shouldUseClientLayout ? "client" : "default");
      setIsCheckingPermission(false);

      // Check access (Centralized)
      if (menu && currentPage) {
        // @ts-expect-error - menu key from Redux is not strictly typed against PERMISSIONS object
        const pageConfig = PERMISSIONS[menu]?.pages?.[currentPage];
        const permission = pageConfig?.permission;

        if (permission) {
          const hasPermission = (perm: string | string[]) => {
            if (Array.isArray(perm)) {
              return perm.some((p) => userPermissions[p]);
            }
            return userPermissions[perm];
          };

          if (!hasPermission(permission)) {
            history.replace("/not-found");
          }
        }
      }
    } else {
      // If no permissions yet, check if we should preemptively use client layout
      // to prevent flash on page load
      try {
        const userId = document.cookie.match(/userId=([^;]+)/)?.[1];
        const hasClientCookie = userId && document.cookie.includes(`${userId}-clientId=`);

        if (hasClientCookie && isClientTypeUser) {
          setLayout("client");
        } else if (isClientTypeUser && (!userId || !hasClientCookie)) {
          // Only redirect client-type users to account selection if cookies are missing
          // Non-client users should not be redirected
          history.push("/account-selection");
        }
      } catch {
        // Only redirect client-type users on error
        if (isClientTypeUser) {
          history.push("/account-selection");
        }
      }
    }
  }, [userPermissions, isClientTypeUser, location.pathname, history, menu, currentPage]);

  return (
    <LayoutContext.Provider value={{ layout, setLayout, isCheckingPermission }}>
      {children}
    </LayoutContext.Provider>
  );
};
